import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Video, Check } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/site/Reveal";

const IFRAME_ID = "splose-booking-5623b454-fbdb-44d0-a9fc-422d32a758ae";

const LOCATIONS = [
  {
    id: "gold-coast",
    name: "Gold Coast",
    detail: "Unit 12, 151 Cotlew Street, Ashmore QLD 4214",
    icon: MapPin,
  },
  {
    id: "canberra",
    name: "Canberra",
    detail: "Room 6, 310 Thesiger Court, Deakin ACT 2600",
    icon: MapPin,
  },
  {
    id: "telehealth",
    name: "Telehealth",
    detail: "Video appointment — Gold Coast, Brisbane, Canberra and surrounding suburbs",
    icon: Video,
  },
];

export default function Book() {
  const [location, setLocation] = useState(null);
  useEffect(() => {
    const handler = (e) => {
      if (e.origin !== "https://eucalypt-therapy.splose.com") return;
      if (!e.data || !e.data.type) return;
      const f = document.getElementById(IFRAME_ID);
      if (!f || e.source !== f.contentWindow) return;
      if (e.data.type === "sploseIframeResize") {
        const h = e.data.height;
        if (typeof h === "number" && isFinite(h) && h >= 0) {
          f.style.height = h + "px";
        }
      }
      if (e.data.type === "sploseIframeScrollToTop") {
        f.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      if (e.data.type === "sploseBookingConfirmed") {
        const p = e.data.payload || {};
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "splose_booking_confirmed",
          booking_page_uuid: String(p.booking_page_uuid),
          booking_page_name: String(p.booking_page_name),
          service_name: String(p.service_name),
          location_name: String(p.location_name),
          service_price: Number(p.service_price),
        });
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  return (
    <main data-testid="book-page">
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <SectionHeading
            eyebrow="Book an appointment"
            title="Choose a time that suits you"
            lead="Select your service and a time — at our Ashmore (Gold Coast) or Deakin (Canberra) rooms, or by telehealth. Your booking is confirmed instantly."
          />
        </div>
      </section>
      <section className="bg-euca-mist">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 sm:pb-28">
          <Reveal>
            <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-euca">
              Step 1 — Choose your location
            </p>
            <div className="mt-5 grid gap-4 sm:grid-cols-3" data-testid="book-location-picker">
              {LOCATIONS.map((loc) => {
                const active = location && location.id === loc.id;
                return (
                  <button
                    key={loc.id}
                    type="button"
                    onClick={() => setLocation(loc)}
                    data-testid={`book-location-${loc.id}`}
                    className={`rounded-2xl border p-6 text-left transition-all duration-300 ${
                      active
                        ? "border-euca bg-white shadow-md"
                        : "border-euca-line bg-white/70 hover:border-euca-sage hover:bg-white"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <loc.icon className="h-5 w-5 text-euca" strokeWidth={1.5} />
                      {active && <Check className="h-5 w-5 text-euca" />}
                    </div>
                    <p className="mt-3 font-serif text-xl font-medium text-euca-dark">{loc.name}</p>
                    <p className="mt-1 text-sm leading-relaxed text-mutedsoft">{loc.detail}</p>
                  </button>
                );
              })}
            </div>
          </Reveal>

          {location && (
            <Reveal>
              <div className="mt-10">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm text-mutedsoft" data-testid="book-chosen-location">
                    <span className="font-semibold text-charcoal">Step 2 — Booking for {location.name}:</span>{" "}
                    {location.detail}
                  </p>
                  <button
                    type="button"
                    onClick={() => setLocation(null)}
                    data-testid="book-change-location"
                    className="text-sm font-semibold text-euca hover:underline"
                  >
                    Change location
                  </button>
                </div>
                <div className="overflow-hidden rounded-2xl border border-euca-line bg-white p-2 shadow-sm sm:p-4">
                  <iframe
                    id={IFRAME_ID}
                    src="https://eucalypt-therapy.splose.com/online-booking/5623b454-fbdb-44d0-a9fc-422d32a758ae?embedVersion=v2"
                    title="Online booking"
                    name="onlineBookingEmbeded"
                    scrolling="auto"
                    width="100%"
                    height="700"
                    loading="lazy"
                    frameBorder="0"
                    style={{ display: "block", transition: "height 0.3s ease" }}
                    data-testid="booking-iframe"
                  />
                </div>
              </div>
            </Reveal>
          )}
          <p className="mt-6 text-center text-sm text-mutedsoft">
            Prefer to talk first? Call{" "}
            <a href="tel:+61417508502" className="font-semibold text-euca hover:underline" data-testid="book-call-link">
              0417 508 502
            </a>{" "}
            or{" "}
            <Link to="/contact" className="font-semibold text-euca hover:underline" data-testid="book-enquiry-link">
              send an enquiry
            </Link>
            .
          </p>
        </div>
      </section>
    </main>
  );
}
